'use client';

import { IconCircularProgressBar } from '@/icons/IconCircularProgressBar';
import { IconClock } from '@/icons/IconClock';
import { IconCross } from '@/icons/IconCross';
import useHeaderStore from '@/store/header';
import { useOrderStore } from '@/store/order';
import Image from 'next/image';
import { useRouter } from 'next/navigation';

// the last character needs to be removed to get the actual order id
function removeLastCharacter(str: string) {
  return str.slice(0, -1);
}

function getStatusUpdateTime(bufferTime: string, statusUpdateTime: string) {
  if (!bufferTime || !statusUpdateTime) return '';

  const [bufferHours, bufferMinutes] = bufferTime.split(':').map(Number);
  const bufferMS = (bufferHours * 60 + bufferMinutes) * 60000;
  // Force the statusUpdateTime to be in UTC
  const allocatedAt = new Date(statusUpdateTime + ' UTC');
  const endTime = new Date(allocatedAt.getTime() + bufferMS);
  const currentTime = new Date();
  const timeDifference = endTime.getTime() - currentTime.getTime();
  const timeDifferenceInMinutes = Math.ceil(timeDifference / 60000);

  if (timeDifferenceInMinutes <= 0) return '';

  return timeDifferenceInMinutes;
}

export const BopisPanel = () => {
  const router = useRouter();
  const bopisOrders = useOrderStore((state) => state.bopisOrders);
  const isBopisPanelOpen = useHeaderStore((state) => state.isBopisPanelOpen);
  const setIsBopisPanelOpen = useHeaderStore((state) => state.setIsBopisPanelOpen);

  const handleToggle = () => {
    setIsBopisPanelOpen(!isBopisPanelOpen);
  };

  const handleOrderClick = (suborderCode: string) => {
    router.push(`/account/order-details/${removeLastCharacter(suborderCode)}`);
    setIsBopisPanelOpen(false);
  };

  function getOrderCount(status: 'NotCompleted' | 'Completed' | '' = '') {
    if (status === '') return bopisOrders.length;
    return bopisOrders.filter((order) => order.SuborderCode === status).length;
  }

  if (bopisOrders.length === 0) return null;

  return (
    <>
      <button
        onClick={handleToggle}
        className="absolute bottom-40 right-14 z-[9999] h-20 w-20 rounded-full bg-white text-center shadow-[0_6px_16px_rgba(0,0,0,0.1)] transition-all active:scale-90"
      >
        <div className="flex h-full w-full items-center justify-center">
          <IconCircularProgressBar radius={22} strokeWidth={2} percentage={50} color="#EAB308">
            <Image src="/gifs/processing.gif" alt="processing" width={24} height={24} />
          </IconCircularProgressBar>

          <span className="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full bg-red-600 text-sm font-semibold text-white">
            {getOrderCount()}
          </span>
        </div>
      </button>
      <div
        className={`absolute right-0 top-0 h-full w-[340px] max-w-full bg-white transition-all ${
          isBopisPanelOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <header className="mb-2 px-8 pt-6">
          <div className="flex items-center justify-between">
            <p className="text-2xl font-semibold">Order Status</p>

            {isBopisPanelOpen && (
              <button onClick={handleToggle} className="a ml-auto flex h-auto cursor-pointer border-none bg-transparent p-0 text-xl">
                <IconCross className="mr-1 w-4" />
              </button>
            )}
          </div>

          <p className="mt-1 text-lg text-gray-400">Your current order status</p>
        </header>
        <div>
          <ul className="max-h-[calc(100dvh-var(--header-height))] overflow-y-auto px-3 py-2">
            {bopisOrders.map((order) => {
              const readyToPickUpTime = getStatusUpdateTime(order.BufferTime, order.StatusUpdateTime);

              return (
                <li key={order.SuborderCode} className="py-3">
                  <button
                    onClick={() => handleOrderClick(order.SuborderCode)}
                    className="flex w-full cursor-pointer items-center gap-3 py-3 transition-colors duration-200 hover:bg-gray-50"
                    style={{ background: 'none', border: 'none', margin: 0, padding: 0 }}
                  >
                    {order.SuborderStatus === 'Processing' ? (
                      <IconCircularProgressBar radius={15} strokeWidth={2} percentage={50} color="#EAB308">
                        <Image src="/gifs/processing.gif" alt="processing" width={18} height={18} />
                      </IconCircularProgressBar>
                    ) : (
                      <Image src="/gifs/ready-to-pickup.gif" alt="ready to pick up" width={18} height={18} />
                    )}
                    <div>
                      <span className="flex items-center text-lg font-semibold text-black">
                        {order.SuborderStatus}
                        <ChevronIcon className="ml-1 -rotate-90" size={4} />
                      </span>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm text-[#666666]">Order ID#{removeLastCharacter(order.SuborderCode)}</p>
                        {order.SuborderStatus === 'Processing' && readyToPickUpTime && (
                          <p className="whitespace-nowrap text-sm text-[#666666]">
                            <IconClock className="h-3 w-3" /> {getStatusUpdateTime(order.BufferTime, order.StatusUpdateTime)} Mins
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </>
  );
};

const ChevronIcon = ({ className = '', size = 5 }: { className?: string; size?: number }) => {
  return (
    <svg
      className={`h-${size} w-${size} font-semibold text-gray-500 transition-transform duration-200 ${className}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
};
